/**
 * Reload and weapon handling. These are the sounds that sell an animation:
 * mag release, mag out of the well, mag in, seat, bolt, selector, sling swivels,
 * cloth and gear movement, weapon raise/lower, casings hitting the floor.
 *
 * Everything is modal synthesis (metal and polymer as sums of decaying sinusoids)
 * plus filtered/granulated noise (friction, cloth), so each part sounds like an
 * object rather than a filtered click.
 */
import {
  modal, karplus, applyExpDecay, applyAD, bandpass, lowpass, highpass,
  granular, normalise as normArr, addInto, sineSweep, fadeOut,
} from '../synth/dsp.js';
import { noiseSlice } from '../synth/noise.js';
import { bq, gain, playData } from '../synth/nodes.js';

/** Play a JS-generated Float32Array through an optional filter chain. */
function emit(ac, dest, t0, data, g, filters = [], rate = 1) {
  const gn = gain(ac, g);
  let head = gn;
  for (let i = filters.length - 1; i >= 0; i--) { filters[i].connect(head); head = filters[i]; }
  gn.connect(dest);
  playData(ac, data, head, Math.max(0, t0), { rate });
  return gn;
}

const jit = (rng, a = 0.08) => 1 + (rng() * 2 - 1) * a;

/* ---------------------------------------------------------------- metal bits */

/** A short metallic clack — the atom of every reload sound. */
function clack(sr, rng, { f = 2200, seconds = 0.09, tau = 0.018, bright = 1 } = {}) {
  const modes = [
    [f, 1.0, tau],
    [f * 1.62, 0.55 * bright, tau * 0.65],
    [f * 2.41, 0.32 * bright, tau * 0.42],
    [f * 0.53, 0.60, tau * 1.8],
    [f * 0.31, 0.35, tau * 2.6],
  ];
  const data = modal(sr, seconds, modes, rng, 0.06);
  const click = noiseSlice('white', Math.floor(sr * 0.004), rng);
  applyExpDecay(click, sr, 0.0006);
  highpass(click, sr, 2500, 0.7);
  addInto(data, click, 0, 0.7);
  applyExpDecay(data, sr, seconds * 0.35);
  return normArr(data, 1);
}

/** Polymer/plastic knock — duller, faster decay, more low-mid. */
function knock(sr, rng, { f = 900, seconds = 0.07, tau = 0.010 } = {}) {
  const data = modal(sr, seconds, [
    [f, 1.0, tau], [f * 1.41, 0.4, tau * 0.6], [f * 2.2, 0.18, tau * 0.35], [f * 0.6, 0.5, tau * 1.5],
  ], rng, 0.08);
  applyExpDecay(data, sr, seconds * 0.3);
  return normArr(data, 1);
}

/** Sliding friction: filtered noise with a slow amplitude arc. */
function friction(sr, rng, { seconds = 0.14, f = 1600, Q = 1.1, rough = 0.5 } = {}) {
  const n = Math.floor(sr * seconds);
  const data = noiseSlice('white', n, rng);
  for (let i = 0; i < n; i++) {
    const k = i / n;
    let a = Math.sin(Math.PI * k);
    a *= 1 - rough * 0.5 * (0.5 + 0.5 * Math.sin(k * 90 + rng() * 0.01));
    data[i] *= a;
  }
  bandpass(data, sr, f * jit(rng, 0.1), Q);
  return normArr(data, 1);
}

/** Cloth / fabric rustle. Granular so it never sounds like a noise gate. */
function cloth(sr, rng, { seconds = 0.26, level = 1 } = {}) {
  const src = noiseSlice('pink', Math.floor(sr * 0.04), rng);
  bandpass(src, sr, 2400, 0.8);
  applyExpDecay(src, sr, 0.010);
  const data = granular(sr, seconds, src, rng, {
    grains: 22, grainMs: 22, spread: 0.9, pitchJitter: 0.6, gainJitter: 0.85,
  });
  for (let i = 0; i < data.length; i++) {
    const k = i / data.length;
    data[i] *= Math.sin(Math.PI * Math.min(1, k * 1.3)) * level;
  }
  highpass(data, sr, 700, 0.7);
  return normArr(data, 1);
}

/* ------------------------------------------------------------------ *
 * Handling sounds — each returns { duration }
 * ------------------------------------------------------------------ */

export const HANDLING = {
  mag_release(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    emit(ac, dest, t0, clack(sr, rng, { f: 3400 * jit(rng), seconds: 0.05, tau: 0.006, bright: 1.2 }), 0.5 * (o.volume ?? 1), [bq(ac, 'highpass', 900, 0.7)]);
    emit(ac, dest, t0 + 0.004, karplus(sr, 0.06, 2600 * jit(rng), 0.86, rng), 0.14 * (o.volume ?? 1));
    return { duration: 0.10 };
  },

  mag_out(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    const v = o.volume ?? 1;
    emit(ac, dest, t0, friction(sr, rng, { seconds: 0.13, f: 1500, Q: 0.9, rough: 0.7 }), 0.30 * v, [bq(ac, 'highpass', 500, 0.7)]);
    emit(ac, dest, t0 + 0.075, knock(sr, rng, { f: 780 * jit(rng), seconds: 0.08, tau: 0.011 }), 0.38 * v);
    emit(ac, dest, t0 + 0.10, cloth(sr, rng, { seconds: 0.18 }), 0.16 * v);
    return { duration: 0.30 };
  },

  mag_in(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    const v = o.volume ?? 1;
    emit(ac, dest, t0, cloth(sr, rng, { seconds: 0.14 }), 0.18 * v);
    emit(ac, dest, t0 + 0.06, friction(sr, rng, { seconds: 0.09, f: 1900, Q: 1.2, rough: 0.4 }), 0.26 * v);
    emit(ac, dest, t0 + 0.11, knock(sr, rng, { f: 640 * jit(rng), seconds: 0.09, tau: 0.013 }), 0.42 * v);
    return { duration: 0.26 };
  },

  mag_seat(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    const v = o.volume ?? 1;
    // a firm, low, confident thunk — the sound that makes a reload feel finished
    emit(ac, dest, t0, clack(sr, rng, { f: 1250 * jit(rng), seconds: 0.13, tau: 0.020, bright: 0.7 }), 0.55 * v);
    const thud = sineSweep(sr, 0.12, 190, 82, 0.035, rng, 2);
    emit(ac, dest, t0 + 0.001, thud, 0.40 * v, [bq(ac, 'lowpass', 320, 0.9)]);
    emit(ac, dest, t0 + 0.012, karplus(sr, 0.09, 3100 * jit(rng), 0.9, rng), 0.10 * v, [bq(ac, 'highpass', 1500, 0.7)]);
    return { duration: 0.22 };
  },

  bolt_back(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    const v = o.volume ?? 1;
    emit(ac, dest, t0, friction(sr, rng, { seconds: 0.10, f: 2600, Q: 1.5, rough: 0.85 }), 0.34 * v);
    emit(ac, dest, t0 + 0.085, clack(sr, rng, { f: 2900 * jit(rng), seconds: 0.09, tau: 0.011 }), 0.48 * v);
    emit(ac, dest, t0 + 0.088, karplus(sr, 0.14, 1150 * jit(rng), 0.968, rng), 0.16 * v, [bq(ac, 'bandpass', 2200, 1.2)]);
    return { duration: 0.26 };
  },

  bolt_release(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    const v = o.volume ?? 1;
    emit(ac, dest, t0, friction(sr, rng, { seconds: 0.035, f: 3200, Q: 1.8, rough: 0.9 }), 0.24 * v);
    emit(ac, dest, t0 + 0.030, clack(sr, rng, { f: 1750 * jit(rng), seconds: 0.14, tau: 0.022, bright: 1.15 }), 0.68 * v);
    const thud = sineSweep(sr, 0.10, 165, 74, 0.028, rng, 2);
    emit(ac, dest, t0 + 0.031, thud, 0.28 * v, [bq(ac, 'lowpass', 300, 0.9)]);
    emit(ac, dest, t0 + 0.036, karplus(sr, 0.16, 980 * jit(rng), 0.972, rng), 0.15 * v, [bq(ac, 'bandpass', 2600, 1.1)]);
    return { duration: 0.30 };
  },

  charging_handle(ac, dest, t0, rng, o = {}) {
    const r = HANDLING.bolt_back(ac, dest, t0, rng, o);
    HANDLING.bolt_release(ac, dest, t0 + 0.16, rng, o);
    return { duration: r.duration + 0.30 };
  },

  selector(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    emit(ac, dest, t0, clack(sr, rng, { f: 5200 * jit(rng), seconds: 0.03, tau: 0.0035, bright: 1.3 }), 0.30 * (o.volume ?? 1), [bq(ac, 'highpass', 2000, 0.7)]);
    return { duration: 0.06 };
  },

  dryfire(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    emit(ac, dest, t0, clack(sr, rng, { f: 2400 * jit(rng), seconds: 0.06, tau: 0.008, bright: 0.9 }), 0.42 * (o.volume ?? 1));
    return { duration: 0.10 };
  },

  sling(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    const v = o.volume ?? 1;
    emit(ac, dest, t0, cloth(sr, rng, { seconds: 0.30 }), 0.24 * v);
    // swivel jingle: two or three tiny high modal pings scattered in time
    for (let i = 0; i < 3; i++) {
      emit(ac, dest, t0 + 0.02 + rng() * 0.22,
        modal(sr, 0.05, [[5600 * jit(rng, 0.2), 1, 0.012], [8100 * jit(rng, 0.2), 0.5, 0.006]], rng),
        0.055 * v * (0.5 + rng()), [bq(ac, 'highpass', 3000, 0.7)]);
    }
    return { duration: 0.36 };
  },

  cloth(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    emit(ac, dest, t0, cloth(sr, rng, { seconds: 0.22 }), 0.22 * (o.volume ?? 1));
    return { duration: 0.26 };
  },

  gear(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    const v = o.volume ?? 1;
    emit(ac, dest, t0, cloth(sr, rng, { seconds: 0.18 }), 0.14 * v);
    const nPings = 2 + Math.floor(rng() * 2);
    for (let i = 0; i < nPings; i++) {
      emit(ac, dest, t0 + rng() * 0.10,
        modal(sr, 0.04, [[3800 * jit(rng, 0.25), 1, 0.008], [6200 * jit(rng, 0.25), 0.4, 0.004]], rng),
        0.05 * v * (0.4 + rng()), [bq(ac, 'highpass', 2200, 0.7)]);
    }
    return { duration: 0.22 };
  },

  weapon_raise(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    const v = o.volume ?? 1;
    emit(ac, dest, t0, cloth(sr, rng, { seconds: 0.24 }), 0.26 * v);
    emit(ac, dest, t0 + 0.05, friction(sr, rng, { seconds: 0.12, f: 1200, Q: 0.8, rough: 0.5 }), 0.16 * v);
    emit(ac, dest, t0 + 0.14, knock(sr, rng, { f: 900 * jit(rng), seconds: 0.07, tau: 0.009 }), 0.20 * v);
    HANDLING.gear(ac, dest, t0 + 0.02, rng, { volume: 0.7 * v });
    return { duration: 0.34 };
  },

  weapon_lower(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    const v = o.volume ?? 1;
    emit(ac, dest, t0, cloth(sr, rng, { seconds: 0.30 }), 0.22 * v);
    emit(ac, dest, t0 + 0.10, friction(sr, rng, { seconds: 0.15, f: 950, Q: 0.7, rough: 0.4 }), 0.14 * v);
    HANDLING.gear(ac, dest, t0 + 0.08, rng, { volume: 0.6 * v });
    return { duration: 0.40 };
  },

  /** Brass hitting a hard floor: three or four decaying bounces. */
  shell_drop(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    const v = o.volume ?? 1;
    const base = 4200 * jit(rng, 0.18);
    let t = t0 + (o.delay ?? 0.42) + rng() * 0.08;
    let g = 0.30 * v;
    const bounces = 3 + Math.floor(rng() * 3);
    for (let i = 0; i < bounces; i++) {
      const data = modal(sr, 0.10, [
        [base, 1.0, 0.030], [base * 1.51, 0.6, 0.020], [base * 2.13, 0.35, 0.012],
        [base * 0.61, 0.45, 0.045],
      ], rng, 0.05);
      applyExpDecay(data, sr, 0.020);
      normArr(data, 1);
      emit(ac, dest, t, data, g, [bq(ac, 'highpass', 1800, 0.7)], 0.95 + rng() * 0.12);
      t += 0.045 + rng() * 0.055;
      g *= 0.55;
    }
    return { duration: 0.95 };
  },
};

/* ------------------------------------------------------------------ *
 * UI / feedback
 * ------------------------------------------------------------------ */

export const UI_SOUNDS = {
  ui_click(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    emit(ac, dest, t0, modal(sr, 0.05, [[1600, 1, 0.012], [3200, 0.35, 0.006]], rng), 0.22 * (o.volume ?? 1));
    return { duration: 0.08 };
  },
  ui_hover(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    emit(ac, dest, t0, modal(sr, 0.03, [[2600, 1, 0.005]], rng), 0.09 * (o.volume ?? 1));
    return { duration: 0.05 };
  },
  ui_confirm(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    emit(ac, dest, t0, modal(sr, 0.14, [[880, 1, 0.05], [1320, 0.5, 0.04]], rng), 0.18 * (o.volume ?? 1));
    emit(ac, dest, t0 + 0.055, modal(sr, 0.16, [[1320, 1, 0.06], [1760, 0.4, 0.05]], rng), 0.16 * (o.volume ?? 1));
    return { duration: 0.24 };
  },
  ui_deny(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    emit(ac, dest, t0, modal(sr, 0.12, [[220, 1, 0.05], [233, 0.8, 0.05]], rng), 0.20 * (o.volume ?? 1));
    return { duration: 0.16 };
  },
  hitmarker(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    const d = noiseSlice('white', Math.floor(sr * 0.035), rng);
    bandpass(d, sr, 3800, 2.2);
    applyAD(d, sr, 0.0008, 0.007);
    normArr(d, 1);
    emit(ac, dest, t0, d, 0.24 * (o.volume ?? 1));
    emit(ac, dest, t0, modal(sr, 0.05, [[2400, 1, 0.010], [3600, 0.5, 0.006]], rng), 0.16 * (o.volume ?? 1));
    return { duration: 0.07 };
  },
  headshot(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    UI_SOUNDS.hitmarker(ac, dest, t0, rng, o);
    emit(ac, dest, t0 + 0.008, modal(sr, 0.10, [[5200, 1, 0.020], [7800, 0.45, 0.010]], rng), 0.20 * (o.volume ?? 1), [bq(ac, 'highpass', 2500, 0.7)]);
    return { duration: 0.14 };
  },
  kill_confirm(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    const v = o.volume ?? 1;
    emit(ac, dest, t0, modal(sr, 0.20, [[660, 1, 0.07], [990, 0.5, 0.06], [1320, 0.25, 0.05]], rng), 0.20 * v);
    emit(ac, dest, t0 + 0.07, modal(sr, 0.26, [[880, 1, 0.10], [1320, 0.45, 0.08]], rng), 0.17 * v);
    return { duration: 0.34 };
  },
  damage_hit(ac, dest, t0, rng, o = {}) {
    const sr = ac.sampleRate;
    const v = o.volume ?? 1;
    const d = noiseSlice('brown', Math.floor(sr * 0.28), rng);
    lowpass(d, sr, 420, 1.1);
    applyAD(d, sr, 0.003, 0.055);
    normArr(d, 1);
    emit(ac, dest, t0, d, 0.42 * v);
    const th = sineSweep(sr, 0.22, 120, 48, 0.06, rng, 2);
    emit(ac, dest, t0, th, 0.35 * v, [bq(ac, 'lowpass', 220, 0.9)]);
    return { duration: 0.32 };
  },
};

export const _parts = { clack, knock, friction, cloth, emit };
